
/* File: frontend/src/modules/trading/dispatch_order/pages/TradingDispatchOrderEditPage.js */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingDispatchOrderService from "../services/tradingDispatchOrderService";

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const formatQuantity = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number)
    ? number.toLocaleString("en-IN", { maximumFractionDigits: 3 })
    : "-";
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  border: "1px solid #dfe2eb",
  borderRadius: "8px",
  fontSize: "13px",
  background: "#fff",
};

const labelStyle = {
  display: "block",
  marginBottom: "7px",
  color: "#747b8d",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e8eaf2",
  borderRadius: "12px",
  padding: "20px",
  marginBottom: "18px",
};

const headingStyle = {
  margin: "0 0 18px",
  fontSize: "16px",
  fontWeight: 700,
  color: "#252b3b",
};

const tableHeaderStyle = {
  padding: "12px",
  textAlign: "left",
  background: "#f7f8fc",
  color: "#70778a",
  fontSize: "11px",
  textTransform: "uppercase",
  borderBottom: "1px solid #e8eaf2",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "12px",
  fontSize: "13px",
  borderBottom: "1px solid #f0f1f6",
  verticalAlign: "middle",
};

const primaryButtonStyle = {
  border: 0,
  borderRadius: "8px",
  padding: "11px 17px",
  background: "#6254d9",
  color: "#fff",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  border: "1px solid #dfe2eb",
  borderRadius: "8px",
  padding: "10px 14px",
  background: "#fff",
  color: "#374151",
  fontWeight: 600,
  cursor: "pointer",
};

const errorStyle = {
  padding: "12px 14px",
  background: "#fff0f0",
  color: "#b42318",
  borderRadius: "8px",
  fontSize: "13px",
  marginBottom: "16px",
};

const infoStyle = {
  padding: "12px 14px",
  background: "#f0f5ff",
  color: "#244a91",
  borderRadius: "8px",
  fontSize: "13px",
  marginBottom: "16px",
};

const getDateInputValue = (value) => {
  if (!value) return "";

  // Supports YYYY-MM-DD and ISO datetime responses.
  return String(value).slice(0, 10);
};

const getBookingRequired = (value) =>
  value === true ||
  value === 1 ||
  ["YES", "TRUE", "1"].includes(String(value ?? "").toUpperCase());

export default function TradingDispatchOrderEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dispatchOrder, setDispatchOrder] = useState(null);
  const [items, setItems] = useState([]);

  const [plannedDispatchDate, setPlannedDispatchDate] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [transporterBookingRequired, setTransporterBookingRequired] =
    useState(false);
  const [transporterBpId, setTransporterBpId] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [remarks, setRemarks] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const loadDispatchOrder = useCallback(async () => {
    setLoading(true);
    setError("");
    setInfo("");

    try {
      const response =
        await tradingDispatchOrderService.getDispatchOrder(id);

      const data = getResponseData(response);
      const record = data?.dispatch_order || data;

      if (!record || !record.id) {
        throw new Error("Dispatch Order details were not returned by the API.");
      }

      if (String(record.status_code || "").toUpperCase() !== "DRAFT") {
        setDispatchOrder(record);
        setItems([]);
        setError("Only Draft Dispatch Orders can be edited.");
        return;
      }

      const recordItems = Array.isArray(record.items) ? record.items : [];

      setDispatchOrder(record);
      setItems(
        recordItems.map((item) => ({
          ...item,
          planned_dispatch_quantity:
            item.planned_dispatch_quantity ?? "",
        }))
      );

      setPlannedDispatchDate(
        getDateInputValue(record.planned_dispatch_date)
      );
      setDeliveryAddress(record.delivery_address || "");
      setTransporterBookingRequired(
        getBookingRequired(record.transporter_booking_required)
      );
      setTransporterBpId(
        record.transporter_bp_id == null
          ? ""
          : String(record.transporter_bp_id)
      );
      setDeliveryInstructions(record.delivery_instructions || "");
      setRemarks(record.remarks || "");

      if (recordItems.length === 0) {
        setInfo("No dispatch items were returned for this Dispatch Order.");
      }
    } catch (err) {
      setDispatchOrder(null);
      setError(
        getErrorMessage(err, "Unable to load Dispatch Order for editing.")
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDispatchOrder();
  }, [loadDispatchOrder]);

  const handleQuantityChange = (itemId, value) => {
    setItems((current) =>
      current.map((item) =>
        String(item.id ?? item.sales_order_item_id) === String(itemId)
          ? { ...item, planned_dispatch_quantity: value }
          : item
      )
    );
  };

  const plannedItems = useMemo(
    () =>
      items
        .map((item) => ({
          ...item,
          planned_dispatch_quantity:
            item.planned_dispatch_quantity === ""
              ? 0
              : Number(item.planned_dispatch_quantity),
        }))
        .filter((item) => item.planned_dispatch_quantity > 0),
    [items]
  );

  const totalPlannedQuantity = useMemo(
    () =>
      plannedItems.reduce(
        (total, item) => total + item.planned_dispatch_quantity,
        0
      ),
    [plannedItems]
  );

  const validateForm = () => {
    if (!dispatchOrder) {
      return "Dispatch Order details are not loaded.";
    }

    if (String(dispatchOrder.status_code || "").toUpperCase() !== "DRAFT") {
      return "Only Draft Dispatch Orders can be edited.";
    }

    if (!plannedDispatchDate) {
      return "Planned Dispatch Date is required.";
    }

    if (plannedItems.length === 0) {
      return "Enter a planned dispatch quantity for at least one item.";
    }

    for (const item of plannedItems) {
      const quantity = item.planned_dispatch_quantity;
      const remaining = Number(
        item.remaining_quantity_snapshot ??
          item.remaining_quantity ??
          0
      );

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return `Enter a valid planned quantity for ${
          item.item_description || "the item"
        }.`;
      }

      if (quantity > remaining) {
        return `Planned quantity for ${
          item.item_description || "the item"
        } cannot exceed the displayed remaining quantity of ${formatQuantity(
          remaining
        )}.`;
      }
    }

    if (transporterBookingRequired) {
      if (
        !transporterBpId ||
        !Number.isInteger(Number(transporterBpId)) ||
        Number(transporterBpId) <= 0
      ) {
        return "Enter a valid Transporter Business Partner ID.";
      }
    }

    return "";
  };

  const handleSave = async () => {
    setError("");
    setInfo("");

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      planned_dispatch_date: plannedDispatchDate,
      delivery_address: deliveryAddress.trim(),
      transporter_booking_required: transporterBookingRequired,
      transporter_bp_id: transporterBookingRequired
        ? Number(transporterBpId)
        : null,
      delivery_instructions: deliveryInstructions.trim(),
      remarks: remarks.trim(),
      items: plannedItems.map((item) => ({
        sales_order_item_id: Number(item.sales_order_item_id),
        planned_dispatch_quantity: item.planned_dispatch_quantity,
      })),
    };

    setSaving(true);

    try {
      await tradingDispatchOrderService.updateDispatchOrder(id, payload);

      navigate(`/trading/dispatch-orders/${id}`);
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to update the Dispatch Order.")
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "24px", background: "#f7f8fc", minHeight: "100%" }}>
        <p style={{ color: "#747b8d" }}>Loading Dispatch Order...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        background: "#f7f8fc",
        minHeight: "100%",
        fontFamily: "Inter, Arial, sans-serif",
        color: "#252b3b",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "22px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "25px", fontWeight: 700 }}>
            Edit Dispatch Order
          </h1>
          <p style={{ margin: "7px 0 0", color: "#747b8d", fontSize: "13px" }}>
            {dispatchOrder?.dispatch_order_no || `Dispatch Order ${id}`}
          </p>
        </div>

        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate(`/trading/dispatch-orders/${id}`)}
          disabled={saving}
        >
          Cancel
        </button>
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {info && (
        <div role="status" style={infoStyle}>
          {info}
        </div>
      )}

      {!dispatchOrder ? (
        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate("/trading/dispatch-orders")}
        >
          Back to Dispatch Orders
        </button>
      ) : String(dispatchOrder.status_code || "").toUpperCase() !== "DRAFT" ? (
        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate(`/trading/dispatch-orders/${id}`)}
        >
          Back to Details
        </button>
      ) : (
        <>
          <section style={cardStyle}>
            <h2 style={headingStyle}>Sales Order Details</h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                gap: "20px",
              }}
            >
              <div>
                <label style={labelStyle}>Dispatch Order No.</label>
                <div>{dispatchOrder.dispatch_order_no || `DO-${id}`}</div>
              </div>

              <div>
                <label style={labelStyle}>Sales Order No.</label>
                <div>{dispatchOrder.sales_order_no || "—"}</div>
              </div>

              <div>
                <label style={labelStyle}>Customer</label>
                <div>
                  {dispatchOrder.customer_name ||
                    (dispatchOrder.customer_id
                      ? `Customer #${dispatchOrder.customer_id}`
                      : "—")}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <div>{dispatchOrder.status_code || "DRAFT"}</div>
              </div>
            </div>
          </section>

          <section style={cardStyle}>
            <h2 style={headingStyle}>Dispatch Items</h2>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  minWidth: "850px",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Line</th>
                    <th style={tableHeaderStyle}>Item Description</th>
                    <th style={tableHeaderStyle}>Ordered Qty.</th>
                    <th style={tableHeaderStyle}>Dispatched Snapshot</th>
                    <th style={tableHeaderStyle}>Remaining Snapshot</th>
                    <th style={tableHeaderStyle}>Planned Dispatch Qty.</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => {
                    const itemKey =
                      item.id ?? item.sales_order_item_id ?? index;
                    const remaining = Number(
                      item.remaining_quantity_snapshot ??
                        item.remaining_quantity ??
                        0
                    );

                    return (
                      <tr key={itemKey}>
                        <td style={tableCellStyle}>
                          {item.item_seq_no ?? index + 1}
                        </td>

                        <td style={tableCellStyle}>
                          {item.item_description || "—"}
                        </td>

                        <td style={tableCellStyle}>
                          {formatQuantity(item.ordered_quantity)}
                        </td>

                        <td style={tableCellStyle}>
                          {formatQuantity(item.dispatched_quantity_snapshot)}
                        </td>

                        <td style={tableCellStyle}>
                          {formatQuantity(remaining)}
                        </td>

                        <td style={{ ...tableCellStyle, width: "180px" }}>
                          <input
                            type="number"
                            min="0"
                            max={remaining}
                            step="0.001"
                            value={item.planned_dispatch_quantity}
                            onChange={(event) =>
                              handleQuantityChange(
                                itemKey,
                                event.target.value
                              )
                            }
                            style={inputStyle}
                            aria-label={`Planned quantity for ${
                              item.item_description || "item"
                            }`}
                          />

                          {Number(item.planned_dispatch_quantity || 0) >
                            remaining && (
                            <div
                              style={{
                                marginTop: "5px",
                                color: "#b42318",
                                fontSize: "11px",
                              }}
                            >
                              Exceeds displayed remaining quantity
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                padding: "13px",
                background: "#f7f8fc",
                borderRadius: "8px",
                fontSize: "13px",
              }}
            >
              <span style={{ marginRight: "10px", color: "#747b8d" }}>
                Total Planned Quantity
              </span>
              <strong>{formatQuantity(totalPlannedQuantity)}</strong>
            </div>
          </section>

          <section style={cardStyle}>
            <h2 style={headingStyle}>Delivery & Transport Details</h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle} htmlFor="edit-dispatch-date">
                  Planned Dispatch Date *
                </label>
                <input
                  id="edit-dispatch-date"
                  type="date"
                  value={plannedDispatchDate}
                  onChange={(event) =>
                    setPlannedDispatchDate(event.target.value)
                  }
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle} htmlFor="edit-transporter-required">
                  Transporter Booking Required
                </label>
                <select
                  id="edit-transporter-required"
                  value={transporterBookingRequired ? "YES" : "NO"}
                  onChange={(event) => {
                    const required = event.target.value === "YES";
                    setTransporterBookingRequired(required);
                    if (!required) setTransporterBpId("");
                  }}
                  style={inputStyle}
                >
                  <option value="NO">No</option>
                  <option value="YES">Yes</option>
                </select>
              </div>

              {transporterBookingRequired && (
                <div>
                  <label style={labelStyle} htmlFor="edit-transporter-bp-id">
                    Transporter Business Partner ID *
                  </label>
                  <input
                    id="edit-transporter-bp-id"
                    type="number"
                    min="1"
                    step="1"
                    value={transporterBpId}
                    onChange={(event) =>
                      setTransporterBpId(event.target.value)
                    }
                    style={inputStyle}
                    required
                  />
                </div>
              )}
            </div>

            <div style={{ marginTop: "18px" }}>
              <label style={labelStyle} htmlFor="edit-delivery-address">
                Delivery Address
              </label>
              <textarea
                id="edit-delivery-address"
                rows={3}
                value={deliveryAddress}
                onChange={(event) => setDeliveryAddress(event.target.value)}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <div style={{ marginTop: "18px" }}>
              <label style={labelStyle} htmlFor="edit-delivery-instructions">
                Delivery Instructions
              </label>
              <textarea
                id="edit-delivery-instructions"
                rows={2}
                value={deliveryInstructions}
                onChange={(event) =>
                  setDeliveryInstructions(event.target.value)
                }
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <div style={{ marginTop: "18px" }}>
              <label style={labelStyle} htmlFor="edit-dispatch-remarks">
                Remarks
              </label>
              <textarea
                id="edit-dispatch-remarks"
                rows={2}
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>
          </section>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              paddingBottom: "24px",
            }}
          >
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={() => navigate(`/trading/dispatch-orders/${id}`)}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              style={{
                ...primaryButtonStyle,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
              onClick={handleSave}
              disabled={saving || items.length === 0}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
